import { useSelector } from 'react-redux';

import { MOBILE_SCREEN, useDeviceType } from '@travel/traveler-core/hooks';
import { cx } from '@travel/utils';

import WithSkeletonLoading from 'components/WithSkeletonLoading';

import { getIsFetching } from 'store/providerInformation/selectors';

import DetailSummary from '../DetailSummary';
import ShortAddressWithMap from '../ShortAddressWithMap';
import OverviewAccess from './OverviewAccess';
import OverviewAwardsAndCatchphrase from './OverviewAwardsAndCatchphrase';
import OverviewPropertyFacilities from './OverviewPropertyFacilities';
import OverviewReviews from './OverviewReviews';

import styles from './overview.module.scss';

type Props = {
  className?: string;
  /** String defining current provider id */
  pageIdentifier: string;
  /** on what parent page this component lives */
  onPage: 'providerInfoUndated' | 'providerInfoDated';
  /** parent page name for RAT popups */
  pageName: 'hotel:rooms_and_plans_dated_list' | 'hotel:rooms_and_plans_undated_list';
  /** handle click see more review in PC */
  onClickSeeMoreReview?: () => void;
};

function Overview(props: Props) {
  const { className, pageIdentifier, onPage, pageName, onClickSeeMoreReview } = props;

  const isFetching = useSelector(getIsFetching);

  const isMobile = useDeviceType() === MOBILE_SCREEN;

  return (
    <div
      data-testid="providerInformation-overview-wrapper"
      className={cx(styles.wrapper, className)}
    >
      <WithSkeletonLoading
        className={styles.container}
        isLoading={isFetching}
        row={5}
        count={3}
        customStyle="text"
      >
        {!isMobile && (
          <DetailSummary pageName={pageName} pageIdentifier={pageIdentifier} isShare={true} />
        )}
        {isMobile && (
          <>
            {/* awards and catchphrase */}
            <OverviewAwardsAndCatchphrase pageName={pageName} pageIdentifier={pageIdentifier} />

            {/* address with map */}
            <ShortAddressWithMap
              pageName={pageName}
              wrapperClassName={styles.overviewInfoContainer}
            />
          </>
        )}

        <div className={styles.cardWrapper}>
          {/* reviews */}
          <OverviewReviews onPage={onPage} onClickSeeMoreReview={onClickSeeMoreReview} />

          {/* nearby terminal access */}
          {!isMobile && <OverviewAccess />}

          {/* facilities */}
          <OverviewPropertyFacilities />
        </div>
      </WithSkeletonLoading>
    </div>
  );
}

export default Overview;
